import type { EMTProfile, EmployerProfile } from '../types/firestore';

export type NotificationPreferences = {
  emailJobMatches?: boolean;
  emailMessages?: boolean;
  inAppSound?: boolean;
  urgentJobAlerts?: boolean;
  /** Employer: new applicant alerts */
  newApplicantAlerts?: boolean;
  /** Employer: new message alerts */
  newMessageAlerts?: boolean;
};

export type PrivacyPreferences = {
  showInEmployerSearch?: boolean;
  showReadinessScorePublicly?: boolean;
  /** Consent to renewal contact from certified training institutes — OFF unless the EMT turns it on (lib/renewal/instituteContactConsent.ts). */
  institutesMayContact?: boolean;
};

export const DEFAULT_EMT_NOTIFICATIONS: Required<
  Pick<NotificationPreferences, 'emailJobMatches' | 'emailMessages' | 'inAppSound' | 'urgentJobAlerts'>
> = {
  emailJobMatches: true,
  emailMessages: true,
  inAppSound: true,
  urgentJobAlerts: true,
};

export const DEFAULT_EMPLOYER_NOTIFICATIONS: Required<
  Pick<NotificationPreferences, 'newApplicantAlerts' | 'newMessageAlerts'>
> = {
  newApplicantAlerts: true,
  newMessageAlerts: true,
};

export const DEFAULT_PRIVACY: Required<PrivacyPreferences> = {
  showInEmployerSearch: true,
  showReadinessScorePublicly: true,
  institutesMayContact: false,
};

export function readEmtNotificationPrefs(
  profile: EMTProfile | null | undefined,
): Required<Pick<NotificationPreferences, 'emailJobMatches' | 'emailMessages' | 'inAppSound' | 'urgentJobAlerts'>> {
  const n = profile?.preferences?.notifications;
  return {
    emailJobMatches: n?.emailJobMatches ?? profile?.preferences?.jobAlerts ?? DEFAULT_EMT_NOTIFICATIONS.emailJobMatches,
    emailMessages: n?.emailMessages ?? profile?.preferences?.emailNotifications ?? DEFAULT_EMT_NOTIFICATIONS.emailMessages,
    inAppSound: n?.inAppSound ?? DEFAULT_EMT_NOTIFICATIONS.inAppSound,
    urgentJobAlerts: n?.urgentJobAlerts ?? profile?.preferences?.jobAlerts ?? DEFAULT_EMT_NOTIFICATIONS.urgentJobAlerts,
  };
}

export function readEmtPrivacyPrefs(profile: EMTProfile | null | undefined): Required<PrivacyPreferences> {
  const p = profile?.preferences?.privacy;
  return {
    showInEmployerSearch: p?.showInEmployerSearch ?? profile?.preferences?.publicProfile ?? DEFAULT_PRIVACY.showInEmployerSearch,
    showReadinessScorePublicly: p?.showReadinessScorePublicly ?? DEFAULT_PRIVACY.showReadinessScorePublicly,
    // Only an explicit `true` counts as consent; missing / anything else is off.
    institutesMayContact: p?.institutesMayContact === true,
  };
}

export function readEmployerNotificationPrefs(
  profile: EmployerProfile | null | undefined,
): Required<Pick<NotificationPreferences, 'newApplicantAlerts' | 'newMessageAlerts'>> {
  const n = profile?.preferences?.notifications;
  return {
    newApplicantAlerts: n?.newApplicantAlerts ?? DEFAULT_EMPLOYER_NOTIFICATIONS.newApplicantAlerts,
    newMessageAlerts: n?.newMessageAlerts ?? DEFAULT_EMPLOYER_NOTIFICATIONS.newMessageAlerts,
  };
}

/** Build Firestore patch for EMT notification + legacy fields. */
export function emtNotificationPatch(
  current: EMTProfile | null | undefined,
  next: Partial<NotificationPreferences>,
) {
  const merged = { ...readEmtNotificationPrefs(current), ...next };
  return {
    preferences: {
      ...(current?.preferences ?? {}),
      emailNotifications: merged.emailMessages,
      jobAlerts: merged.emailJobMatches,
      notifications: {
        ...(current?.preferences?.notifications ?? {}),
        ...merged,
      },
    },
  };
}

export function emtPrivacyPatch(
  current: EMTProfile | null | undefined,
  next: Partial<PrivacyPreferences>,
) {
  const merged = { ...readEmtPrivacyPrefs(current), ...next };
  return {
    preferences: {
      ...(current?.preferences ?? {}),
      publicProfile: merged.showInEmployerSearch,
      privacy: {
        ...(current?.preferences?.privacy ?? {}),
        ...merged,
      },
    },
  };
}

export function employerNotificationPatch(
  current: EmployerProfile | null | undefined,
  next: Partial<NotificationPreferences>,
) {
  const merged = { ...readEmployerNotificationPrefs(current), ...next };
  return {
    preferences: {
      ...(current?.preferences ?? {}),
      notifications: {
        ...(current?.preferences?.notifications ?? {}),
        ...merged,
      },
    },
  };
}
